import React, { useState, useContext, useRef } from 'react';
import { UserContext } from '../context/UserContext';
import { Navigate } from 'react-router-dom';
import apiConfig from '../config/apiConfig';
import Cropper from 'react-easy-crop';
import './EditProfile.css';

const EditProfile = () => {
  const { user, login } = useContext(UserContext);
  const initialBio = (user && user.playerCharacter && user.playerCharacter.bio) || '';
  const initialIcon = (user && user.playerCharacter && user.playerCharacter.icon)
    ? `${apiConfig.resources}${user.playerCharacter.icon}`
    : '';

  const [bio, setBio] = useState(initialBio);
  const [profilePicFile, setProfilePicFile] = useState(null);
  const [previewPic, setPreviewPic] = useState(initialIcon);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const bioRef = useRef(null);

  // Cropping states
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [showCropper, setShowCropper] = useState(false);

  if (!user || !user.playerCharacter) {
    return <Navigate to="/profile" replace />;
  }

  // Helper to load an image.
  const createImage = (url) =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', (error) => reject(error));
      image.setAttribute('crossOrigin', 'anonymous');
      image.src = url;
    });

  // Returns a blob and URL for the cropped image.
  const getCroppedImg = async (imageSrc, pixelCrop) => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;
    const ctx = canvas.getContext('2d');

    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );

    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Canvas is empty'));
          return;
        }
        blob.name = 'cropped.jpeg';
        const fileUrl = window.URL.createObjectURL(blob);
        resolve({ blob, fileUrl });
      }, 'image/jpeg');
    });
  };

  // When a file is selected, set the image source and show the cropper.
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const imageDataUrl = URL.createObjectURL(file);
      setImageSrc(imageDataUrl);
      setShowCropper(true);
    }
  };

  // When the user clicks "Crop Image," generate the cropped image.
  const showCroppedImage = async () => {
    try {
      const { blob, fileUrl } = await getCroppedImg(imageSrc, croppedAreaPixels);
      setPreviewPic(fileUrl);
      setProfilePicFile(blob);
      setShowCropper(false);
    } catch (e) {
      console.error(e);
      setError('Failed to crop image.');
    }
  };

  // Insert Markdown syntax based on the type.
  const insertMarkdown = (syntax) => {
    const textarea = bioRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = bio.substring(start, end);
    let insertText = '';
    if (syntax === 'bold') {
      insertText = `**${selectedText}**`;
    } else if (syntax === 'italic') {
      insertText = `*${selectedText}*`;
    } else if (syntax === 'code') {
      insertText = `\`${selectedText}\``;
    } else if (syntax === 'color') {
      const color = prompt("Enter a color (e.g., red or #FF0000):", "red");
      if (!color) return;
      insertText = `[color=${color}]${selectedText}[/color]`;
    } else if (syntax === 'image') {
      const imageUrl = prompt("Enter image URL:");
      if (!imageUrl) return;
      insertText = `![Image](${imageUrl})`;
    }
    const newText = bio.substring(0, start) + insertText + bio.substring(end);
    setBio(newText);
    setTimeout(() => {
      textarea.focus();
      textarea.selectionStart = textarea.selectionEnd = start + insertText.length;
    }, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (bio === initialBio && !profilePicFile) {
      setError("No changes to update.");
      return;
    }
    setLoading(true);
    let updatedBio = bio;
    let updatedIcon = initialIcon;
    try {
      if (bio !== initialBio) {
        const bioResponse = await fetch(apiConfig.updateProfile, {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ bio })
        });
        if (!bioResponse.ok) {
          let errorData = {};
          try {
            errorData = await bioResponse.json();
          } catch (e) {
            errorData = {};
          }
          setError(errorData.message || 'Failed to update bio.');
          setLoading(false);
          return;
        }
        try {
          const parsedBioData = await bioResponse.json();
          updatedBio = (parsedBioData && parsedBioData.bio) ? parsedBioData.bio : bio;
        } catch (err) {
          updatedBio = bio;
        }
      }
      if (profilePicFile) {
        const formData = new FormData();
        formData.append('icon', profilePicFile);
        const iconResponse = await fetch(apiConfig.updateIcon, {
          method: 'PATCH',
          credentials: 'include',
          body: formData
        });
        if (!iconResponse.ok) {
          let errorData = {};
          try {
            errorData = await iconResponse.json();
          } catch (e) {
            errorData = {};
          }
          setError(errorData.message || 'Failed to update profile picture.');
          setLoading(false);
          return;
        }
        try {
          const parsedIconData = await iconResponse.json();
          if (parsedIconData && parsedIconData.icon) {
            updatedIcon = `${apiConfig.resources}${parsedIconData.icon}`;
          }
        } catch (err) {
          // If parsing fails, retain the old icon.
        }
      }
      const updatedCharacter = {
        ...user.playerCharacter,
        bio: updatedBio,
        icon: updatedIcon
      };
      login({ ...user, playerCharacter: updatedCharacter });
      setLoading(false);
    } catch (err) {
      setLoading(false);
      setError('Error: ' + err.message);
    }
  };

  return (
    <div className="page-content">
      <h2>Edit Profile</h2>
      {error && <div className="error-message">{error}</div>}
      <form onSubmit={handleSubmit}>
        <div>
          <label>Profile Picture:</label>
          <input type="file" accept="image/*" onChange={handleFileChange} />
          {showCropper && imageSrc && (
            <div className="cropper-wrapper">
              <div className="cropper-container">
                <Cropper
                  image={imageSrc}
                  crop={crop}
                  zoom={zoom}
                  aspect={2 / 3}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={(croppedArea, croppedAreaPixels) => setCroppedAreaPixels(croppedAreaPixels)}
                  cropShape="rect"
                  showGrid={false}
                />
              </div>
              <button type="button" onClick={showCroppedImage} className="crop-button">
                Crop Image
              </button>
            </div>
          )}
          {previewPic && !showCropper && (
            <div>
              <img
                src={previewPic}
                alt="Preview"
                style={{ width: '150px', height: '225px', objectFit: 'cover' }}
              />
            </div>
          )}
        </div>
        <div>
          <label>Bio:</label>
          <div className="markdown-toolbar">
            <button type="button" onClick={() => insertMarkdown('bold')}>Bold</button>
            <button type="button" onClick={() => insertMarkdown('italic')}>Italic</button>
            <button type="button" onClick={() => insertMarkdown('code')}>Code</button>
            <button type="button" onClick={() => insertMarkdown('color')}>Color</button>
            <button type="button" onClick={() => insertMarkdown('image')}>Image</button>
          </div>
          <textarea
            ref={bioRef}
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            rows="10"
            cols="50"
            placeholder="Enter your bio (Markdown supported)"
          />
        </div>
        <button type="submit" disabled={loading}>
          {loading ? 'Updating...' : 'Update Profile'}
        </button>
      </form>
    </div>
  );
};

export default EditProfile;
